import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import { APP_NAME, ROUTES } from '@utils/constants';

const RefundPolicyPage: React.FC = () => {
  const lastUpdated = 'December 14, 2025';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link
            to={ROUTES.HOME}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          {/* Page header */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Refund Policy</h1>
            <p className="text-gray-600">
              Last updated: {lastUpdated}
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-8 prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Overview</h2>
              <p className="text-gray-700 mb-4">
                {APP_NAME} operates as a multi-party marketplace connecting professional photographers
                with customers. This refund policy explains how refunds are handled for different types
                of transactions on our platform.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-6">
                <p className="text-blue-700">
                  <strong>Important:</strong> Different refund policies apply to subscription payments
                  (paid to {APP_NAME}) and photo purchases (paid to individual Photographers).
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Subscription Refunds (Photographers)</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">1.1 General Policy</h3>
              <p className="text-gray-700 mb-4">
                <strong>Subscription payments are non-refundable.</strong> An active paid subscription
                is required to use the platform &mdash; we do not offer free trials. When you subscribe
                to a {APP_NAME} plan (Basic, Pro, or Enterprise), you are purchasing access to our
                platform and services for the billing period.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">1.2 Cancellation</h3>
              <p className="text-gray-700 mb-4">
                You may cancel your subscription at any time through your account settings or by
                contacting support. Upon cancellation:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>You will continue to have access until the end of your current billing period</li>
                <li>No partial refunds will be issued for the remaining days in your billing cycle</li>
                <li>Your subscription will not renew at the end of the current period</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">1.3 Exceptional Circumstances</h3>
              <p className="text-gray-700 mb-4">
                In rare cases, we may consider refund requests for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Duplicate charges due to technical errors</li>
                <li>Extended service outages exceeding 48 hours</li>
                <li>Billing errors on our part</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Such requests will be reviewed on a case-by-case basis. Contact info@pixel-share.com
                with details of your situation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Photo Purchase Refunds (Customers)</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">2.1 Payment Structure</h3>
              <p className="text-gray-700 mb-4">
                When you purchase photos on {APP_NAME}, your payment is distributed as follows:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Photographer:</strong> Receives approximately 94% of the purchase price (minus payment processing fees)</li>
                <li><strong>{APP_NAME}:</strong> Retains a 3% platform fee</li>
                <li><strong>Payment Processor:</strong> Stripe collects standard processing fees (~2.9% + $0.30)</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">2.2 Refund Authority</h3>
              <p className="text-gray-700 mb-4">
                <strong>Photo purchase refunds are at the Photographer's discretion.</strong> Since the
                majority of your payment goes directly to the Photographer, refund decisions for photo
                purchases are made by the individual Photographer who sold the photos.
              </p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">2.3 How to Request a Photo Refund</h3>
              <ol className="list-decimal pl-6 text-gray-700 mb-4">
                <li>Contact the Photographer directly through their gallery or event page</li>
                <li>Explain your reason for requesting a refund</li>
                <li>The Photographer will review your request and respond</li>
                <li>If approved, the refund will be processed through Stripe</li>
              </ol>

              <h3 className="text-xl font-medium text-gray-800 mb-3">2.4 Common Refund Scenarios</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Scenario</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Typical Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700 border-b">Wrong photos purchased</td>
                      <td className="px-4 py-3 text-sm text-gray-700 border-b">Photographer's discretion</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700 border-b">Technical issues with download</td>
                      <td className="px-4 py-3 text-sm text-gray-700 border-b">Usually resolved with re-download link</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700 border-b">Quality concerns</td>
                      <td className="px-4 py-3 text-sm text-gray-700 border-b">Photographer's discretion</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700 border-b">Duplicate purchase</td>
                      <td className="px-4 py-3 text-sm text-gray-700 border-b">Usually refunded</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700 border-b">Unauthorized transaction</td>
                      <td className="px-4 py-3 text-sm text-gray-700 border-b">Contact support immediately</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-medium text-gray-800 mb-3 mt-6">2.5 Dispute Resolution</h3>
              <p className="text-gray-700 mb-4">
                If you are unable to reach a resolution with the Photographer, you may contact {APP_NAME}
                support for assistance. We will attempt to mediate between parties but cannot guarantee
                a refund will be issued.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Chargebacks and Disputes</h2>

              <h3 className="text-xl font-medium text-gray-800 mb-3">3.1 Before Filing a Chargeback</h3>
              <p className="text-gray-700 mb-4">
                We strongly encourage you to contact us or the Photographer before initiating a
                chargeback with your bank or credit card company. Chargebacks:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Are time-consuming and may not be resolved in your favor</li>
                <li>May result in your account being suspended</li>
                <li>Can often be resolved faster through direct communication</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-800 mb-3">3.2 Fraudulent Chargebacks</h3>
              <p className="text-gray-700 mb-4">
                Filing a fraudulent chargeback (claiming a transaction was unauthorized when it was not)
                is a violation of our Terms of Service and may result in:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Permanent account suspension</li>
                <li>Collection of outstanding amounts</li>
                <li>Legal action in severe cases</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Processing Time</h2>
              <p className="text-gray-700 mb-4">
                When a refund is approved:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Credit/Debit Cards:</strong> 5-10 business days to appear on your statement</li>
                <li><strong>Digital Wallets:</strong> 1-3 business days</li>
                <li><strong>Bank Transfers:</strong> 5-7 business days</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Processing times depend on your financial institution and are outside of our control.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                For refund-related inquiries:
              </p>
              <ul className="list-none text-gray-700 mb-4">
                <li><strong>Email:</strong> info@pixel-share.com</li>
                <li><strong>Subject Line:</strong> "Refund Request - [Order ID]"</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Please include your order details, email address used for purchase, and reason for the
                refund request to help us assist you faster.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Policy Updates</h2>
              <p className="text-gray-700 mb-4">
                This Refund Policy may be updated from time to time. Changes will be posted on this page
                with an updated "Last Updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Related Policies</h2>
              <ul className="list-disc pl-6 text-gray-700">
                <li><Link to={ROUTES.TERMS_OF_SERVICE} className="text-primary-600 hover:text-primary-700">Terms of Service</Link></li>
                <li><Link to={ROUTES.PRIVACY_POLICY} className="text-primary-600 hover:text-primary-700">Privacy Policy</Link></li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RefundPolicyPage;
